import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { ProgressSpinnerOverlayComponent } from '../../../shared/components/progress-spinner-overlay/progress-spinner-overlay.component';
import { NotificationPreference } from '../../models/notification-preference';
import { NotificationType } from '../../models/notification-type';
import { NotificationTypeId } from '../../models/notification-type-id';

/**
 * Reduces a stored number to the bare 10 digits the masked control works in, dropping formatting
 * and a leading US country code. Anything else is returned digits-only: accepted while unchanged (see
 * formatUnlessOnFile) and format-checked once the patient edits it.
 */
function normalizePhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/**
 * Stands in for the ngx-mask directive's own validator (disabled in the template), which cannot be
 * told to skip an on-file value. Reports under the same `mask` key so the existing mat-error renders.
 */
function phoneFormat(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? '';
  return !value || /^\d{10}$/.test(value) ? null : { mask: { requiredMask: '(000) 000-0000', actualValue: value } };
}

/**
 * Lets the value that was on file at load through a format check. EHR-imported data can be malformed
 * (a 7-digit number, say), and since an on-file value cannot be cleared either, enforcing its format
 * would block every save — even just unticking a checkbox — until the patient invented contact data.
 * Anything the patient edits is checked. Format only: cannotClear still stops it being blanked.
 */
function formatUnlessOnFile(format: ValidatorFn, onFile: string): ValidatorFn {
  return control => (onFile && control.value === onFile ? null : format(control));
}

/**
 * A contact value that arrived populated cannot be cleared: the API rejects it (56950). Kept apart from
 * `required`, which follows the ticked channels, so the field isn't marked required just for being on
 * file and the patient is told why they can't blank it ("update it instead") rather than "required".
 */
function cannotClear(onFile: string): ValidatorFn {
  return control => (onFile && !control.value ? { onFile: true } : null);
}

interface NotificationFormValue {
  email: string;
  phone: string;
  notificationTypes: Record<string, boolean>;
}

@Component({
  selector: 'app-notification-preferences-form',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    NgxMaskDirective,
    ReactiveFormsModule,
    ProgressSpinnerOverlayComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './notification-preferences-form.component.html',
  styleUrl: './notification-preferences-form.component.scss',
})
export class NotificationPreferencesFormComponent implements OnDestroy {
  notificationTypes: NotificationType[] | null = null;
  notificationForm!: FormGroup;
  private _notificationPreferences: NotificationPreference | null = null;
  private initialFormValue: NotificationFormValue | null = null;
  private subscribe?: Subscription;

  @Input() set notificationType(value: NotificationType[] | null) {
    if (!value) return;
    this.notificationTypes = value;
    this.syncForm();
  }

  @Input() set notificationPreferences(value: NotificationPreference | null) {
    if (!value) return;
    this._notificationPreferences = value;
    this.syncForm();
  }

  @Input() showSaveButton: boolean = true;

  // Driven by the store rather than latched locally on submit: a rejected save must leave the form
  // editable, and a flag only ever set to true blocks it behind the overlay forever.
  //
  // Also locks the form while saving. The save overlay is dismissible, and a successful save re-feeds
  // `notificationPreferences`, which rebuilds the form: anything typed after dismissal would otherwise
  // be silently replaced by the server's copy.
  @Input() set isSaving(value: boolean) {
    this._isSaving = value;
    this.applySavingLock();
  }
  get isSaving(): boolean {
    return this._isSaving;
  }
  private _isSaving = false;

  /** Form-level message for a save the API rejected, e.g. the no-clear rule naming a field. */
  @Input() errorMessage: string | null = null;
  @Output() saveNotificationPreferencesEvent = new EventEmitter<NotificationPreference>();
  @Output() cancelNotificationPreferencesEvent = new EventEmitter<void>();

  get phoneControl(): FormControl {
    return this.notificationForm.get('phone') as FormControl;
  }

  private syncForm(): void {
    if (!this.notificationTypes || !this._notificationPreferences) return;

    const preferences = this._notificationPreferences;
    // Stored numbers come from the EHR import into an NVARCHAR(20) column, so they can arrive
    // formatted ('(740) 555-1212') or country-coded. ngx-mask formats the *display* but writes
    // nothing back, so normalize here — otherwise a value the patient never typed fails the
    // 10-digit format check and locks the whole form.
    const phoneOnLoad = normalizePhone(preferences.mobilePhoneNumber);
    // Trimmed for the same reason: stray whitespace from the import would otherwise fail
    // Validators.email, and a whitespace-only value would show a format error while not being required.
    const emailOnLoad = preferences.emailAddress?.trim() ?? '';

    const notificationTypeControls: { [key: string]: FormControl<boolean> } = {};
    this.notificationTypes.forEach(type => {
      const isChecked = preferences.notificationTypes.some(
        selected => selected.notificationTypeId === type.notificationTypeId
      );
      notificationTypeControls[type.name] = new FormControl<boolean>(isChecked, { nonNullable: true });
    });

    // Both contact values may be null: a proxy inherits the patient's consent while the contact
    // backfills only reach Self links, so live consent with a blank field is a routine arrival
    // state. Rendered empty and left to the conditional validators, which is what blocks Save.
    this.notificationForm = new FormGroup({
      // Format and no-clear validators live on the control and survive checkbox toggles, because
      // setRequired only adds/removes `required`. Each reports a key the template renders, so Save is
      // never blocked silently.
      email: new FormControl<string>(emailOnLoad, {
        nonNullable: true,
        validators: [formatUnlessOnFile(Validators.email, emailOnLoad), cannotClear(emailOnLoad)],
      }),
      phone: new FormControl<string>(phoneOnLoad, {
        nonNullable: true,
        validators: [formatUnlessOnFile(phoneFormat, phoneOnLoad), cannotClear(phoneOnLoad)],
      }),
      notificationTypes: new FormGroup(notificationTypeControls),
    });

    this.initialFormValue = this.notificationForm.getRawValue();

    const notificationTypesControl = this.notificationForm.get('notificationTypes');
    if (!notificationTypesControl) return;

    //syncForm re-runs whenever either input changes, so drop the previous subscription first
    this.subscribe?.unsubscribe();
    this.subscribe = notificationTypesControl.valueChanges.subscribe((types: Record<string, boolean>) => {
      this.updateValidators(types);
    });

    this.updateValidators(notificationTypesControl.value);

    // The rebuilt group starts enabled; carry over the lock if a save is still in flight.
    this.applySavingLock();
  }

  private applySavingLock(): void {
    if (!this.notificationForm) return;

    if (this._isSaving) {
      this.notificationForm.disable({ emitEvent: false });
    } else if (this.notificationForm.disabled) {
      this.notificationForm.enable({ emitEvent: false });
    }
  }

  private updateValidators(selected: Record<string, boolean>): void {
    const emailControl = this.notificationForm.get('email');
    const phoneControl = this.notificationForm.get('phone');

    if (!emailControl || !phoneControl) return;

    // Required only when the patient consents to that channel; an on-file value is held by cannotClear.
    // 56950 keys the phone rule on Phone *or* TextMessage. The TextMessage branch is dormant until SMS
    // is enabled: selectNotificationTypes (and the settings page) filter that type out today, so the
    // form never receives it. Kept so the rule is already in place when the filter is lifted.
    this.setRequired(emailControl, this.isSelected(selected, NotificationTypeId.Email));
    this.setRequired(
      phoneControl,
      this.isSelected(selected, NotificationTypeId.Phone) || this.isSelected(selected, NotificationTypeId.TextMessage)
    );

    emailControl.updateValueAndValidity();
    phoneControl.updateValueAndValidity();
  }

  /**
   * Checkbox controls are keyed by `NotificationType.name`, which is display data the seed owns.
   * Resolve the key from the id instead, which is the contract (see NotificationTypeId).
   */
  private isSelected(selected: Record<string, boolean>, id: NotificationTypeId): boolean {
    const type = this.notificationTypes?.find(candidate => candidate.notificationTypeId === id);
    return !!type && selected[type.name] === true;
  }

  /**
   * Toggles only `required`. setValidators would replace the whole array, dropping the format
   * validators the control was built with.
   */
  private setRequired(control: AbstractControl, required: boolean): void {
    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
  }

  getSelectedNotificationTypeIds(): number[] {
    if (!this.notificationForm || !this.notificationForm.get('notificationTypes')) return [];

    const notificationTypes = this.notificationTypes;
    if (!notificationTypes) return [];

    const selectedIds: number[] = [];
    const formValues = this.notificationForm.get('notificationTypes')?.value || {};

    notificationTypes.forEach(type => {
      if (formValues[type.name]) {
        selectedIds.push(type.notificationTypeId);
      }
    });

    return selectedIds;
  }

  updateNotificationPreferences(): void {
    // Every Save for this form funnels through here (its own, the settings page's, the schedule dialog's).
    if (this.isSaving) {
      return;
    }

    if (this.notificationForm.invalid) {
      // Material renders mat-error only when the control is touched or the form was submitted, and
      // neither happens here: the blank-contact state arrives pristine, and both Save buttons call
      // this method rather than submitting the form. Without this the user clicks Save and sees
      // nothing at all.
      this.notificationForm.markAllAsTouched();
      return;
    }

    if (JSON.stringify(this.notificationForm.getRawValue()) !== JSON.stringify(this.initialFormValue)) {
      const notificationTypes = this.notificationTypes;
      if (!notificationTypes) return;

      const updatedPreferences: NotificationPreference = {
        // back out as null, not '': the API stores what it is sent, and the 56948 pre-exposure
        // checks look for NULL, so a blank saved as an empty string would hide that user from them
        emailAddress: this.notificationForm.value.email || null,
        mobilePhoneNumber: this.notificationForm.value.phone || null,
        notificationTypes: notificationTypes.filter(type =>
          this.getSelectedNotificationTypeIds().includes(type.notificationTypeId)
        ),
      };

      this.saveNotificationPreferencesEvent.emit(updatedPreferences);
    } else {
      this.cancelNotificationPreferencesEvent.emit();
    }
  }

  ngOnDestroy(): void {
    this.subscribe?.unsubscribe();
  }
}
